import { useState } from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import { IModal } from "Pages/Admin/StatusMessages/StatusMessages";
import styles from "Pages/Admin/StatusMessages/Components/Modals.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";

interface IProps {
    modal: IModal;
    onSetModal: (modal: IModal) => void;
    getMessages: (ignore?: boolean) => Promise<void>;
}

export interface IStatusMessage {
    Status_type: string;
    Expires_at: string;
    Status_message: string;
    Issued_at: string;
    Issued_by: string;
}

export default function AddModal({ modal, onSetModal, getMessages }: IProps) {
    // Time value used for initial value of the expires at input
    const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
    const offset = oneHourFromNow.getTimezoneOffset();
    // Subtract the offset then convert to ISO string, to get a date string
    // in the format yyyy-mm-ddThh:mm (no seconds, ms, or Z at the end) for
    // use in datetime-local input
    oneHourFromNow.setMinutes(oneHourFromNow.getMinutes() - offset);
    const formattedDate = oneHourFromNow.toISOString().slice(0, 16);

    const defaultMessage = {
        Status_type: "Info",
        Expires_at: formattedDate,
        Status_message: "",
    };

    const [message, setMessage] = useState(defaultMessage);

    const [userContext] = useUserContext();
    const { handleFormChange } = useHelpers();
    const { post } = useFetch();
    const { newAlert } = useAlertContext();

    const toggle = () => {
        onSetModal("");
        setMessage(defaultMessage);
    };

    const addMessage = async () => {
        // Add the offset back to the date before sending to back end
        const date = new Date(message.Expires_at);
        const dateString = (message.Expires_at = date.toISOString());

        const formatStatusType = (type: string) => {
            switch (type) {
                case "Info":
                    return "info";
                case "Warning":
                    return "warning";
                case "Error":
                    return "danger";
                default:
                    return "info";
            }
        };

        const addingMessage = {
            ...message,
            Expires_at: dateString,
            Issued_by: userContext.userId,
            Status_type: formatStatusType(message.Status_type),
        };

        console.log(`Adding message: `, message);

        const response = await post("/api/admin/system_status", addingMessage);

        let data: Response | IStatusMessage;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(`Add status message response: `, data);

        if (response.ok) {
            newAlert({
                message: "Status message added",
                color: "success",
            });
            getMessages();
        } else {
            newAlert({
                message: "Error adding status message",
                color: "danger",
            });
        }
        toggle();
    };

    const getSelectInputBgColor = (type: string) => {
        switch (type) {
            case "Info":
                return "var(--bs-info)";
            case "Warning":
                return "var(--bs-warning)";
            case "Error":
                return "var(--bs-danger)";
            default:
                return "";
        }
    };

    return (
        <Modal className="addModal" toggle={toggle} isOpen={modal === "add"}>
            <ModalHeader toggle={toggle}>Add Status Message</ModalHeader>
            <ModalBody>
                <Form>
                    {/* Type */}
                    <FormGroup row>
                        <Col className={styles.labelCol} sm={3}>
                            <Label id="type" type="select">
                                Type:{" "}
                            </Label>
                        </Col>
                        <Col className={styles.labelCol} sm={9}>
                            <Input
                                id="Status_type"
                                name="Status_type"
                                type="select"
                                value={message.Status_type}
                                onChange={(e) =>
                                    handleFormChange(e, setMessage)
                                }
                                style={{
                                    backgroundColor: getSelectInputBgColor(
                                        message.Status_type
                                    ),
                                }}
                                required
                            >
                                <option value="Info" className={"bg-info"}>
                                    Info
                                </option>
                                <option value="Warning" className="bg-warning">
                                    Warning
                                </option>
                                <option value="Error" className="bg-danger">
                                    Error
                                </option>
                            </Input>
                        </Col>
                    </FormGroup>
                    {/* Expires At */}
                    <FormGroup row>
                        <Col className={styles.labelCol} sm={3}>
                            <Label id="Expires_at" type="select">
                                Expires at:{" "}
                            </Label>
                        </Col>
                        <Col sm={9}>
                            <Input
                                id="Expires_at"
                                name="Expires_at"
                                type="datetime-local"
                                value={message.Expires_at}
                                onChange={(e) =>
                                    handleFormChange(e, setMessage)
                                }
                            ></Input>
                        </Col>
                    </FormGroup>{" "}
                    {/* Message */}
                    <FormGroup row>
                        <Col sm={3}>
                            <Label
                                className={styles.labelCol}
                                id="Status_message"
                                type="select"
                            >
                                Message:{" "}
                            </Label>
                        </Col>
                        <Col sm={9}>
                            <Input
                                className={styles.textarea}
                                id="Status_message"
                                name="Status_message"
                                type="textarea"
                                value={message.Status_message}
                                onChange={(e) =>
                                    handleFormChange(e, setMessage)
                                }
                                minLength={2}
                                maxLength={3000}
                                required
                            ></Input>
                        </Col>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={addMessage}>
                    Add
                </Button>
            </ModalFooter>
        </Modal>
    );
}
